import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";
import { maskPassportNumber } from "@/app/lib/utils/maskPassportNumber";
import {
  allTravellerAgeGroups,
  type Traveller,
  type TravellerAgeGroup,
} from "@/types/types";

import { TravellerSelectionButton } from "./TravellersSelectionButton";

type Props = {
  onSlotClick: (ageGroup: TravellerAgeGroup, index: number) => void;
  renderAdditionalServiceControls?: (traveller: Traveller) => ReactNode;
};

export function TravellersSelection({
  onSlotClick,
  renderAdditionalServiceControls,
}: Props) {
  const t = useTranslations();
  const { data: travellers } = useQuery(travellersQueryOptions());

  const travellersById = useMemo(() => {
    const map: Record<string, Traveller> = {};
    for (const traveller of travellers || []) {
      map[traveller.id] = traveller;
    }
    return map;
  }, [travellers]);

  const { travellerSlots } = useTravellersSelectionContext();

  return (
    <ul className="space-y-2">
      {allTravellerAgeGroups.map((ageGroup) =>
        travellerSlots[ageGroup].map((travellerId, index) => (
          <li key={`${ageGroup}-${index}`}>
            <TravellerSelectionButton
              traveller={travellerId ? travellersById[travellerId] : null}
              onClick={() => {
                onSlotClick(ageGroup, index);
              }}
              renderAdditionalServicesControls={
                travellerId
                  ? () =>
                      renderAdditionalServiceControls?.(
                        travellersById[travellerId],
                      )
                  : undefined
              }
            >
              <div className="flex gap-1.5">
                <span>
                  {t("travellers.travellerTypeN", {
                    type: ageGroup,
                    number: index + 1,
                  })}
                </span>
                {travellerId ? (
                  <>
                    <span>&bull;</span>
                    <span>
                      {maskPassportNumber(
                        travellersById[travellerId].passportNumber,
                      )}
                    </span>
                  </>
                ) : null}
              </div>
            </TravellerSelectionButton>
          </li>
        )),
      )}
    </ul>
  );
}
